import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSearchParams } from 'react-router-dom';
import { cbtAPI } from '../../api';

const OfflineExport = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [passcode, setPasscode] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const handleToggle = () => setIsCollapsed(!isCollapsed);

    useEffect(() => {
        const cbtDataId = searchParams.get("cbt_data_id");
        if (cbtDataId) {
            setPasscode(cbtDataId);
        }
    }, [searchParams]);

    const handleExport = async (e) => {
        e.preventDefault();

        if (!passcode.trim()) {
            toast.error("Please enter a CBT passcode");
            return;
        }

        setLoading(true);

        try {
            const response = await cbtAPI.exportOfflinePackage(passcode.trim());

            // Check if response is successful (status in 200-299 range)
            if (!response || response.status < 200 || response.status >= 300) {
                throw new Error(`Server returned ${response?.status || 'unknown'} status`);
            }

            // Check if we actually have data
            if (!response.data || response.data.size === 0) {
                throw new Error("Received empty file from server");
            }

            const blob = response.data;
            
            // Extract filename from Content-Disposition header
            let filename = null;
            const disposition = response.headers?.["content-disposition"];
            
            if (disposition) {
                // Try filename* first (RFC 5987)
                const filenameStar = disposition.match(/filename\*=(?:UTF-8'')?([^;\r\n"]+)/i);
                if (filenameStar && filenameStar[1]) {
                    filename = decodeURIComponent(filenameStar[1]);
                } else {
                    // Fallback to filename
                    const filenameMatch = disposition.match(/filename="?([^;\r\n"]+)"?/i);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1];
                    }
                }
            }
            
            // If no filename from header, use a default
            if (!filename) {
                filename = `cbt_export_${passcode.trim()}.cbthost`;
            }

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            window.URL.revokeObjectURL(url);

            toast.success("Offline package downloaded successfully");

        } catch (error) {
            console.error("Export error:", error);
            
            // Show specific error message
            if (error.response?.status === 403) {
                toast.error("You don't have permission to export this CBT");
            } else if (error.response?.status === 404) {
                toast.error("CBT not found. Please check the passcode");
            } else if (error.message === "Received empty file from server") {
                toast.error("Server returned empty file. No questions available for export");
            } else {
                toast.error(error.response?.data?.detail || error.message || "Failed to export offline package");
            }
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
            
            <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                    <div className="p-2 bg-gradient-to-r from-green-500 to-teal-600 rounded-xl">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                    </div>
                                    Offline Export
                                </h1>
                                <p className="text-gray-600 mt-2">Download an encrypted offline package for a CBT</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Export CBT Package
                            </h2>
                        </div>

                        <div className="p-6">
                            <form onSubmit={handleExport}>
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            CBT Passcode *
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                            value={passcode}
                                            onChange={(e) => setPasscode(e.target.value)}
                                            placeholder="Enter the CBT passcode (e.g., CBT-12345)"
                                            required
                                            disabled={loading}
                                        />
                                        <p className="mt-2 text-sm text-gray-500">
                                            You must own this CBT to export it. The passcode is the CBT data ID.
                                        </p>
                                    </div>

                                    <div className="flex justify-end pt-4 border-t border-gray-100">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl hover:from-green-700 hover:to-teal-700 transition-all duration-200 flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Exporting...
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    Export Offline Package
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Optional info card */}
                    <div className="mt-6 bg-blue-50 rounded-2xl border border-blue-100 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            About Offline Packages
                        </h3>
                        <p className="text-gray-600 text-sm">
                            The exported file is encrypted and can only be used with the offline software.
                            It contains all questions, answers, and metadata. Only MCQ questions are included.
                        </p>
                    </div>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default OfflineExport;